import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/i18n";

const CONSENT_KEY = "sl_cookie_consent";
const CONSENT_MAX_AGE_DAYS = 180;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object" || !data.timestamp) return null;
    const ageDays = (Date.now() - data.timestamp) / 86400000;
    if (ageDays > CONSENT_MAX_AGE_DAYS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeConsent(prefs) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      necessary: true,
      preferences: Boolean(prefs.preferences),
      statistics: Boolean(prefs.statistics),
      timestamp: Date.now(),
      version: 1,
    }));
  } catch { /* ignore */ }
}

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      onClick={onChange}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
        disabled ? "bg-[#2d7a4a] opacity-60 cursor-not-allowed" : checked ? "bg-[#2d7a4a]" : "bg-[#1a2e20] border border-[#1a4a2e]"
      }`}
      aria-pressed={checked}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          checked || disabled ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function CookieBanner() {
  const { lang } = useLang();
  const it = lang === "it";

  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ preferences: false, statistics: false });

  useEffect(() => {
    if (!readConsent()) setVisible(true);
  }, []);

  useEffect(() => {
    const open = () => {
      const existing = readConsent();
      if (existing) {
        setPrefs({ preferences: Boolean(existing.preferences), statistics: Boolean(existing.statistics) });
      }
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener("openCookieSettings", open);
    return () => window.removeEventListener("openCookieSettings", open);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    setCustomize(false);
  }, []);

  const acceptAll = () => {
    writeConsent({ preferences: true, statistics: true });
    close();
  };

  const rejectAll = () => {
    writeConsent({ preferences: false, statistics: false });
    close();
  };

  const savePrefs = () => {
    writeConsent(prefs);
    close();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={it ? "Informativa cookie" : "Cookie notice"}
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto rounded-xl border border-[#1a4a2e] bg-[#0f2518]/95 backdrop-blur-md shadow-2xl shadow-black/50 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Cookie className="w-5 h-5 text-[#d4a056]" />
            <h2 className="text-[#e8f0e8] font-bold uppercase tracking-wider text-sm">
              {it ? "La tua privacy" : "Your privacy"}
            </h2>
          </div>
          <button
            data-testid="cookie-close-btn"
            onClick={rejectAll}
            aria-label={it ? "Chiudi e continua con i soli cookie tecnici" : "Close and continue with technical cookies only"}
            className="text-[#8faa8f] hover:text-[#e8f0e8] transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-[#c0d4c0] text-sm leading-relaxed mb-4">
          {it
            ? "Utilizziamo cookie e tecnologie simili: quelli tecnici, necessari al funzionamento del sito, sono sempre attivi. Con il tuo consenso possiamo utilizzare anche cookie di preferenza e statistici. Chiudendo questo banner con la X proseguirai la navigazione con i soli cookie tecnici. Puoi modificare o revocare le tue scelte in qualsiasi momento dal link \"Gestisci Cookie\" nel footer."
            : "We use cookies and similar technologies: technical ones, required for the website to work, are always active. With your consent we may also use preference and statistics cookies. If you close this banner with the X, you will continue browsing with technical cookies only. You can change or withdraw your choices at any time via the \"Manage Cookies\" link in the footer."}{" "}
          <Link
            to="/cookie-policy"
            data-testid="cookie-policy-banner-link"
            className="text-[#d4a056] underline hover:text-[#e0b86e] transition-colors"
          >
            Cookie Policy
          </Link>
        </p>

        {customize && (
          <div className="space-y-3 mb-4" data-testid="cookie-customize-panel">
            <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-[#0a1a0f]/60 border border-[#1a4a2e]">
              <div>
                <span className="text-sm text-[#e8f0e8] font-semibold block">
                  {it ? "Cookie tecnici necessari" : "Strictly necessary cookies"}
                </span>
                <span className="text-xs text-[#8faa8f]">
                  {it
                    ? "Indispensabili per il funzionamento del sito (lingua, consenso, sicurezza). Sempre attivi."
                    : "Essential for the website to function (language, consent, security). Always active."}
                </span>
              </div>
              <Toggle checked disabled testId="cookie-toggle-necessary" />
            </div>
            <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-[#0a1a0f]/60 border border-[#1a4a2e]">
              <div>
                <span className="text-sm text-[#e8f0e8] font-semibold block">
                  {it ? "Cookie di preferenza" : "Preference cookies"}
                </span>
                <span className="text-xs text-[#8faa8f]">
                  {it
                    ? "Memorizzano scelte facoltative per migliorare la tua esperienza."
                    : "Store optional choices to improve your experience."}
                </span>
              </div>
              <Toggle
                checked={prefs.preferences}
                onChange={() => setPrefs((p) => ({ ...p, preferences: !p.preferences }))}
                testId="cookie-toggle-preferences"
              />
            </div>
            <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-[#0a1a0f]/60 border border-[#1a4a2e]">
              <div>
                <span className="text-sm text-[#e8f0e8] font-semibold block">
                  {it ? "Cookie statistici" : "Statistics cookies"}
                </span>
                <span className="text-xs text-[#8faa8f]">
                  {it
                    ? "Ci aiutano a capire come viene usato il sito, in forma aggregata."
                    : "Help us understand how the website is used, in aggregate form."}
                </span>
              </div>
              <Toggle
                checked={prefs.statistics}
                onChange={() => setPrefs((p) => ({ ...p, statistics: !p.statistics }))}
                testId="cookie-toggle-statistics"
              />
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Accetta e Rifiuta: pari evidenza grafica (Linee Guida Garante 10/06/2021) */}
          <button
            data-testid="cookie-accept-all-btn"
            onClick={acceptAll}
            className="flex-1 bg-[#2d7a4a] text-white font-bold uppercase tracking-wider py-3 px-4 text-sm rounded-lg hover:bg-[#3a9960] transition-all active:scale-[0.98]"
          >
            {it ? "Accetta tutti" : "Accept all"}
          </button>
          <button
            data-testid="cookie-reject-btn"
            onClick={rejectAll}
            className="flex-1 bg-[#1a2e20] border border-[#2d7a4a] text-[#e8f0e8] font-bold uppercase tracking-wider py-3 px-4 text-sm rounded-lg hover:bg-[#24402c] transition-all active:scale-[0.98]"
          >
            {it ? "Rifiuta" : "Reject"}
          </button>
          {customize ? (
            <button
              data-testid="cookie-save-prefs-btn"
              onClick={savePrefs}
              className="flex-1 bg-transparent border border-[#d4a056] text-[#d4a056] font-bold uppercase tracking-wider py-3 px-4 text-sm rounded-lg hover:bg-[#d4a056]/10 transition-all active:scale-[0.98]"
            >
              {it ? "Salva preferenze" : "Save preferences"}
            </button>
          ) : (
            <button
              data-testid="cookie-customize-btn"
              onClick={() => setCustomize(true)}
              className="flex-1 bg-transparent border border-[#d4a056] text-[#d4a056] font-bold uppercase tracking-wider py-3 px-4 text-sm rounded-lg hover:bg-[#d4a056]/10 transition-all active:scale-[0.98]"
            >
              {it ? "Personalizza" : "Customise"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
